/**
 * 재무 모듈 공통 — 거래처/프로젝트/결재 상세 모달의 단일 소스.
 *
 * <p>기존엔 각 탭이 같은 modal state·dialog 마운트·결재 상세 fetch 코드를
 * 통째로 복붙해 쓰고 있었음. 한 곳(Provider)에서 dialog 들을 마운트하고,
 * 셀 컴포넌트에서 hook 으로 open 함수만 받아 호출하도록 단일화.
 *
 * <p>사용:
 * <pre>
 *   // FinancePage 의 메인 영역을 한 번만 감싸준다
 *   <FinanceLinkProvider>
 *     <CurrentTab />
 *   </FinanceLinkProvider>
 *
 *   // 어떤 탭에서든 셀에서:
 *   <ProjectCell channelId={row.channelId} channelName={row.channelName} />
 *   <VendorCell  vendorId={row.vendorId}   vendorName={row.vendorName} />
 *   <TransactionTitleCell row={row} />
 * </pre>
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Box, Chip, Tooltip } from "@mui/material";
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import FolderSpecialRoundedIcon      from "@mui/icons-material/FolderSpecialRounded";

import { api } from "../../../util/api";
import { notify } from "../../../util/toast";
import { approvalApi } from "../../approval/api/approvalApi";
import ApprovalDetail from "../../approval/ApprovalDetail";
import LoadingDots from "../../../component/common/LoadingDots";
import DetailModal from "../../../component/common/DetailModal";

import VendorDetailDialog  from "./vendor/VendorDetailDialog";
import ProjectDetailDialog from "./ProjectDetailDialog";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const FinanceLinkContext = createContext(null);

export function useFinanceLinks() {
    const ctx = useContext(FinanceLinkContext);
    if (!ctx) throw new Error("useFinanceLinks 는 <FinanceLinkProvider/> 안에서만 호출하세요");
    return ctx;
}

export default function FinanceLinkProvider({ children, onAfterApproval }) {
    const [vendor, setVendor]     = useState({ open: false, id: null });
    const [project, setProject]   = useState({ open: false, id: null });
    const [approval, setApproval] = useState({ open: false, data: null, loading: false });
    const [myId, setMyId] = useState(null);

    useEffect(() => {
        api.get(`${API_BASE}/employees/me`).then(r => setMyId(r?.id)).catch(() => {});
    }, []);

    const openVendor   = useCallback((id) => { if (id) setVendor({ open: true, id }); }, []);
    const openProject  = useCallback((id) => { if (id) setProject({ open: true, id }); }, []);
    const openApproval = useCallback(async (id) => {
        if (!id) return;
        setApproval({ open: true, data: null, loading: true });
        try {
            const data = await approvalApi.detail(id);
            setApproval({ open: true, data, loading: false });
        } catch {
            setApproval({ open: false, data: null, loading: false });
            notify("결재 정보를 불러오지 못했습니다.", "error");
        }
    }, []);

    const closeApproval = () => setApproval({ open: false, data: null, loading: false });

    return (
        <FinanceLinkContext.Provider value={{ openVendor, openProject, openApproval, myId }}>
            {children}

            <VendorDetailDialog
                open={vendor.open} vendorId={vendor.id}
                onClose={() => setVendor({ open: false, id: null })} />

            <ProjectDetailDialog
                open={project.open} channelId={project.id}
                onClose={() => setProject({ open: false, id: null })} />

            <DetailModal
                open={approval.open}
                onClose={closeApproval}
                title="결재 상세"
                maxWidth={1500}
            >
                {approval.loading && <LoadingDots compact />}
                {approval.data && (
                    <ApprovalDetail
                        approval={approval.data}
                        myId={myId}
                        onRefresh={onAfterApproval}
                        onClose={closeApproval}
                    />
                )}
            </DetailModal>
        </FinanceLinkContext.Provider>
    );
}

/* ─── 공통 셀 컴포넌트 ───────────────────────────────────── */

/** 프로젝트 채널 셀 — chip 으로 표시, 클릭 시 프로젝트 상세 모달 */
export function ProjectCell({ channelId, channelName, fallback = "—" }) {
    const { openProject } = useFinanceLinks();
    if (!channelName) return <>{fallback}</>;
    return (
        <Chip
            icon={<FolderSpecialRoundedIcon sx={{ fontSize: 12 }} />}
            label={channelName}
            size="small"
            onClick={channelId ? () => openProject(channelId) : undefined}
            sx={{
                bgcolor: "#dbeafe", color: "#2563eb", fontWeight: 600, fontSize: 11,
                cursor: channelId ? "pointer" : "default",
            }} />
    );
}

/** 거래처 셀 — 이름 텍스트, vendorId 있으면 파란색 underline-hover */
export function VendorCell({ vendorId, vendorName, fallback = "—", sx }) {
    const { openVendor } = useFinanceLinks();
    if (!vendorName) return <>{fallback}</>;
    return (
        <Box component="span"
            onClick={vendorId ? () => openVendor(vendorId) : undefined}
            sx={{
                cursor: vendorId ? "pointer" : "default",
                color: vendorId ? "#2563eb" : "inherit",
                fontWeight: 600,
                "&:hover": vendorId ? { textDecoration: "underline" } : {},
                ...sx,
            }}>
            {vendorName}
        </Box>
    );
}

/**
 * 거래 항목명 셀 — fromApproval 이면 결재 클릭 가능.
 * @param {{ row: { title, fromApproval, approvalId } }} props
 */
export function TransactionTitleCell({ row }) {
    const { openApproval } = useFinanceLinks();
    // 열어볼 결재(approvalId)가 실제로 있을 때만 결재 링크로 표시 — 없으면 평범한 제목
    if (!row?.approvalId) return <>{row?.title}</>;
    return (
        <Tooltip title="클릭하여 결재 상세 보기">
            <Box component="span"
                onClick={() => openApproval(row.approvalId)}
                sx={{
                    cursor: "pointer", color: "#2563eb",
                    display: "inline-flex", alignItems: "center", gap: 0.5,
                    "&:hover": { textDecoration: "underline" },
                }}>
                <AssignmentTurnedInRoundedIcon sx={{ fontSize: 13 }} />
                {row.title}
            </Box>
        </Tooltip>
    );
}

/**
 * "결재 시스템 연동" chip — fromApproval 일 때 카테고리 자리에 표시.
 * 클릭 시 결재 상세.
 */
export function ApprovalLinkChip({ row }) {
    const { openApproval } = useFinanceLinks();
    // 열어볼 결재(approvalId)가 실제로 있을 때만 연동 칩 노출 (죽은 칩 방지)
    if (!row?.approvalId) return null;
    return (
        <Chip
            icon={<AssignmentTurnedInRoundedIcon sx={{ fontSize: 11 }} />}
            label="결재 시스템 연동"
            size="small"
            onClick={() => openApproval(row.approvalId)}
            sx={{
                bgcolor: "#eff6ff", color: "#2563eb", fontWeight: 600, fontSize: 11,
                cursor: "pointer",
            }} />
    );
}
